import { keywordsMeta } from "@/lib/seo/keywords";
import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoRail } from "@/components/site/foto/PhotoRail";
import { coverPhotoFor, galleryPhotos, photosBy } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  imageGallery,
  faqPage,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("produkt");

const PATH = "/foto/produktfoto";
const TITLE = "Produktfoto och produktfotografering för e-handel | Fenny";
const DESCRIPTION =
  "Produktfoto för e-handel, katalog och annons — packshot på vit botten, miljöbilder och detaljer. Enhetlig bildstandard och offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är en packshot?",
    a: "En packshot är produkten fotograferad ren mot vit eller neutral bakgrund, rakt utan störande element. Det är standardbilden i e-handelsflöden och marknadsplatser, och den bild som kräver mest exakthet i ljus, färg och beskärning.",
  },
  {
    q: "Hur många produkter hinner ni fotografera per dag?",
    a: "Med en fast uppsättning för likartade produkter hinner vi 30–80 artiklar per dag i ett vinkelläge. Produkter som kräver ny ljussättning, styling eller flera vinklar tar längre tid — då landar dagskapaciteten ofta på 10–25 artiklar.",
  },
  {
    q: "Kan ni matcha vår befintliga bildstandard?",
    a: "Ja. Vi utgår från era befintliga bilder och återskapar ljus, vinkel, beskärning och marginaler så att nya artiklar passar in i flödet utan att sticka ut.",
  },
  {
    q: "Behöver ni skicka produkterna i förväg?",
    a: "Helst. Vi vill ha produkterna någon dag innan så att vi hinner packa upp, rengöra och testljussätta. Vid färskvaror och stora objekt fotograferar vi på plats hos er i stället.",
  },
  {
    q: "Vad kostar produktfoto?",
    a: "Vi offererar per uppdrag med utgångspunkt i antal artiklar, antal vinklar per artikel och hur mycket styling och retusch som krävs. Fler artiklar sänker styckpriset kraftigt eftersom uppsättningen bara byggs en gång.",
  },
];

export const Route = createFileRoute("/foto/produktfoto")({
  head: () => ({
    meta: [
      ...keywordsMeta("/foto/produktfoto"),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Produktfoto — Fenny Foto" },
      { property: "og:description", content: "Packshot, miljöbilder och detaljfoto för e-handel och katalog." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vad kostar produktfoto och hur många artiklar hinner ni per dag?",
            answer:
              "Produktfoto offereras per uppdrag utifrån antal artiklar, antal vinklar och nivå på styling och retusch. Med en fast uppsättning för likartade produkter fotograferar vi 30–80 artiklar per dag i ett vinkelläge, och 10–25 artiklar när varje produkt kräver ny ljussättning. Vi levererar packshot på ren botten, miljöbilder och detaljbilder i samma bildstandard. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Produktfoto",
            path: PATH,
            serviceType: "Produktfotografering",
            description: "Produktfotografering för e-handel, katalog och annons: packshot, miljö och detalj.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: TITLE,
            path: PATH,
            description: "Produktfoto och studiobilder ur Fennys portfolio.",
            images: galleryPhotos.slice(0, 12),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Produktfoto",
              "Packshot",
              "E-handelsbilder",
              "Studiofotografering",
              "Fotograf Stockholm",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Produktfoto", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Produktfoto"
      title={<>Produktbilder som säljer i flödet — och håller i tryck.</>}
      lede="Packshot på ren botten, miljöbilder som visar sammanhang och detaljbilder som svarar på köparens sista fråga. Vi sätter en bildstandard och håller den artikel efter artikel, så att sortimentet ser ut att komma från samma butik."
      tldr={
        <p>
          Produktfoto i tre nivåer: <strong>packshot</strong> (ren botten),{" "}
          <strong>miljöbild</strong> (produkten i sammanhang) och{" "}
          <strong>detalj</strong> (material och funktion). Kapacitet{" "}
          <strong>30–80 artiklar per dag</strong> vid enhetlig uppsättning.
          Offert per uppdrag.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Produktfilm", to: "/produktfilm" }}
      trackingId="foto_produktfoto"
      stills={false}
      videos={false}
      afterHero={
        <PhotoRail
          photos={photosBy("studio").slice(0, 10)}
          eyebrow="Studio"
          heading="Kontrollerat ljus, exakt färg"
        />
      }
      bullets={[
        { title: "En standard, hela sortimentet", body: "Samma marginaler, vinklar och färgtemperatur genom hela leveransen." },
        { title: "Färgsäkert", body: "Vi kalibrerar mot produkten så att kunden får den nyans som faktiskt levereras." },
        { title: "Filformat som passar", body: "Webboptimerat och tryckfärdigt i samma leverans, med filnamn kopplade till artikelnummer." },
      ]}
      process={{
        heading: "Från kartong till publicerad artikel",
        steps: [
          { label: "Artikellista", body: "Vi går igenom sortiment, vinklar per artikel och befintlig bildstandard.", duration: "vecka 1" },
          { label: "Testljus", body: "En testartikel fotograferas och godkänns innan serien startar.", duration: "1 dag" },
          { label: "Serieproduktion", body: "Artiklarna fotograferas i samma uppsättning.", duration: "1–3 dagar" },
          { label: "Retusch", body: "Frilägg, dammborttagning, färgjustering och beskärning.", duration: "2–5 dagar" },
          { label: "Leverans", body: "Filnamn per artikelnummer, redo att importera i e-handeln.", duration: "vid slutleverans" },
        ],
      }}
      sections={[
        {
          id: "packshot-eller-miljo",
          heading: "Hur många bilder behöver en artikel?",
          body: (
            <p>
              Tumregel för e-handel: en packshot rakt framifrån, en till två
              alternativa vinklar, en detaljbild och en miljöbild. Fem bilder per
              artikel täcker de flesta invändningar en köpare har innan
              köpknappen. Har produkten en funktion som behöver visas i rörelse
              kompletterar vi med{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">
                produktfilm
              </a>{" "}
              från samma uppsättning.
            </p>
          ),
        },
        {
          id: "kostnad",
          heading: "Vad som styr priset",
          body: (
            <p>
              Antal artiklar, antal vinklar, hur mycket styling produkten kräver
              och graden av retusch. Volym är den enskilt största hävstången —
              uppsättningen byggs en gång och sedan är varje ytterligare artikel
              betydligt billigare. Se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/vad-kostar-en-fotograf">
                vad kostar en fotograf
              </a>
              .
            </p>
          ),
        },
      ]}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Ska sortimentet fotograferas?",
        body: "Skicka artikellistan så återkommer vi med kapacitet, tidplan och fast pris.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_produktfoto" defaultAssignment="produktfoto" />}
    />
  );
}
